import { Button } from '@framework/ui/atoms/Button';
import { GeneralModal } from '@framework/ui/elements/GeneralModal';
import { ViewModelOperation, type ViewModelEntity } from '@view-model/domain/view-model';
import { useCallback, useState } from 'react';
import { ViewModelIndividualPermissionLocales } from './types';
import type { ViewModelIndividualPermissions, WorkspaceMembers } from './types';
import { WorkspaceMemberRole } from '@workspace/domain/workspace';
import { generateDefaultUserIcon } from '@framework/utils';
import type { UserId, ViewModelId, ViewModelIndividualPermissionRole } from 'balus-common';
import type { UserPublicProfile } from '@user/PublicProfile';
import { toast } from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';

type Props = {
  isOpen: boolean;
  onClose(): void;
  viewModel: ViewModelEntity;
  workspaceMembers: WorkspaceMembers;
  guestUserIds: Set<UserId>;
  individualPermissions: ViewModelIndividualPermissions;
  onAddMember(): void;
  onRemoveMember(): void;
};

export const MemberEditModal: React.FC<Props> = ({
  isOpen,
  onClose,
  viewModel,
  workspaceMembers,
  guestUserIds,
  individualPermissions,
  onAddMember,
  onRemoveMember,
}: Props) => {
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <GeneralModal
      isOpen={isOpen}
      onClose={handleClose}
      title={`${viewModel.name.toString()}の権限`}
      submitButton={null}
      cancelButton={
        <Button color="secondary" onClick={handleClose}>
          閉じる
        </Button>
      }
      containerClassName="overflow-scroll"
      containerStyle={{ maxHeight: '50vh' }}
    >
      <div className="flex justify-end p-2">
        <button
          className="rounded border border-solid border-brand px-2 py-1 text-sm text-brand hover:bg-gray-200"
          onClick={onAddMember}
        >
          メンバーを追加
        </button>
        <button
          className="ml-2 rounded border border-solid border-red-700 px-2 py-1 text-sm text-red-700 hover:bg-gray-200"
          onClick={onRemoveMember}
        >
          メンバーを削除
        </button>
      </div>

      <div className="text-lg font-bold">ビューモデルのメンバー</div>
      <div className="px-2 pb-6 pt-2">
        {individualPermissions.length === 0 ? (
          <div className="flex justify-center px-2 py-4">ビューモデルに個別に権限設定されたメンバーはいません。</div>
        ) : (
          individualPermissions.map(([profile, role]) => (
            <MemberItem
              key={profile.id}
              viewModelId={viewModel.id}
              profile={profile}
              role={role}
              isGuest={guestUserIds.has(profile.id)}
            />
          ))
        )}
      </div>

      <div className="text-lg font-bold">ワークスペースのメンバー</div>
      <ul className="px-2 pb-6 pt-2">
        {workspaceMembers.map(([profile, role]) => (
          <li key={profile.id} className="flex flex-row items-center py-2">
            <span className="flex size-6 overflow-hidden rounded-full">
              <Icon profile={profile} />
            </span>
            <span className="grow">
              <span className="mx-1 truncate">{profile.name}</span>
              {guestUserIds.has(profile.id) && (
                <span className="m-1 rounded-lg bg-gray-400 px-2 text-xs leading-6">ゲスト</span>
              )}
            </span>

            <span className="w-16 justify-end">{WorkspaceMemberRole.getLocalString(role)}</span>
          </li>
        ))}
      </ul>
    </GeneralModal>
  );
};

type ItemProps = {
  viewModelId: ViewModelId;
  profile: UserPublicProfile;
  role: ViewModelIndividualPermissionRole;
  isGuest: boolean;
};

const MemberItem: React.FC<ItemProps> = ({ viewModelId, profile, role, isGuest }: ItemProps) => {
  const [saved, setSaved] = useState(false);

  const handleChange = useCallback(
    async (event: React.ChangeEvent<HTMLSelectElement>) => {
      const role = event.target.value as ViewModelIndividualPermissionRole;
      try {
        await ViewModelOperation.updateIndividualPermission(viewModelId, profile.id, role);
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
        toast.success('ロールを変更しました');
      } catch (e) {
        toast.error('ロールの変更に失敗しました');
        throw e;
      }
    },
    [profile.id, viewModelId]
  );

  return (
    <li className="flex flex-row items-center py-2">
      <span className="flex size-6 overflow-hidden rounded-full">
        <Icon profile={profile} />
      </span>
      <span className="grow">
        <span className="mx-1 truncate">{profile.name}</span>
        {isGuest && <span className="m-1 rounded-lg bg-gray-400 px-2 text-xs leading-6">ゲスト</span>}
      </span>

      <span className="flex w-30 justify-end">
        {saved && (
          <span className="mr-2 font-bold leading-6 text-green-700">
            <FontAwesomeIcon icon={faCheck} />
          </span>
        )}

        <select className="form-select w-24 py-1 text-sm" defaultValue={role} onChange={handleChange}>
          {Object.entries(ViewModelIndividualPermissionLocales).map(([role, label]) => (
            <option key={role} value={role}>
              {label}
            </option>
          ))}
        </select>
      </span>
    </li>
  );
};

type IconProps = {
  profile: UserPublicProfile;
};

const Icon: React.FC<IconProps> = ({ profile }: IconProps) => {
  return profile.imageUrl ? (
    <img className="size-full object-cover" src={profile.imageUrl} alt={profile.name} />
  ) : (
    <div className="size-full object-cover">
      {generateDefaultUserIcon({
        id: profile.id,
        name: profile.name,
        size: 24,
      })}
    </div>
  );
};
